import { Loader2, Sparkles } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { ConversationContextUsage } from "@/hooks/useConversationContextUsage";

interface Props {
  usage: ConversationContextUsage;
  /** Se fornecido, exibe botão "Resumir e continuar" no tooltip quando nível danger. */
  onCompact?: () => void;
  compactPending?: boolean;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

export function ConversationContextIndicator({ usage, onCompact, compactPending }: Props) {
  const pct = Math.min(100, Math.round(usage.ratio * 100));
  const ringHex =
    usage.level === "danger" ? "#ef4444" : usage.level === "warn" ? "#f59e0b" : "currentColor";
  const colorClass =
    usage.level === "danger"
      ? "text-red-500"
      : usage.level === "warn"
      ? "text-amber-500"
      : "text-tertiary";

  const radius = 7;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - Math.min(1, usage.ratio));

  const compactButton = onCompact ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!compactPending) onCompact();
      }}
      disabled={compactPending}
      className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-brand-primary text-black text-[11px] font-medium hover:opacity-90 disabled:opacity-60 disabled:cursor-wait"
    >
      {compactPending ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <Sparkles className="w-3 h-3" />
      )}
      {compactPending ? "Resumindo..." : "Resumir e continuar em nova conversa"}
    </button>
  ) : null;

  const tooltip =
    usage.level === "danger" ? (
      <>
        <p className="font-medium">A janela de contexto do modelo é limitada.</p>
        <p className="mt-1">
          Esta conversa está próxima do limite ({pct}% de {formatTokens(usage.contextWindow)} tokens).
          Gere um resumo e continue em uma nova conversa para manter a qualidade das respostas.
        </p>
        {usage.modelChanged && usage.measuredModelLabel && (
          <p className="mt-1 opacity-80">Medição feita com {usage.measuredModelLabel}.</p>
        )}
        {compactButton}
      </>
    ) : (
      <>
        <p>Percentual da janela de contexto utilizado nesta conversa.</p>
        <p className="mt-1 opacity-80">
          {formatTokens(usage.lastInputTokens)} / {formatTokens(usage.contextWindow)} tokens — medido pelo provedor.
        </p>
        {usage.modelChanged && usage.measuredModelLabel && (
          <p className="mt-1 opacity-80">Medição feita com {usage.measuredModelLabel}.</p>
        )}
        {usage.level === "warn" && compactButton}
      </>
    );

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`inline-flex items-center gap-1.5 px-1.5 py-1 rounded-md hover:bg-hover cursor-default ${colorClass}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" className="shrink-0">
              <circle cx="8" cy="8" r={radius} fill="none" stroke="hsl(var(--border))" strokeWidth="2" />
              <circle
                cx="8"
                cy="8"
                r={radius}
                fill="none"
                stroke={ringHex}
                strokeWidth="2"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
                transform="rotate(-90 8 8)"
              />
            </svg>
            <span className="text-xs font-medium tabular-nums">
              {usage.level === "danger"
                ? `${pct}% · prepare-se para iniciar uma nova conversa`
                : `${pct}% do contexto`}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs z-[100]" side="top">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

